import { useEffect, useRef } from 'react'
import { Order, OrderItem, Package, ProductBundle, ProductMove, ProductMovePack } from 'depoto-core'
import { DepotoCore } from 'depoto-core'
import { Address as AddressType } from 'depoto-core'
import { Fn } from 'depoto-core'
import { OrderItemForReturn, PackageUpdate } from '../store/core/reducer'
import { CommonItem } from './hooks/usePaginated'

export const wait = async (timeMs = 100) => {
  return new Promise(resolve => setTimeout(resolve, timeMs))
}

export const printTicket = async (pack: { ticketUrl: string; carrier: { id: string } }, core?: DepotoCore) => {
  console.log(`Recieved command to print ticket for package ${pack.carrier?.id}: ${pack.ticketUrl}`)

  // const isZasilkovna = pack.carrier && pack.carrier.id && pack.carrier.id.includes('zasilkovna') // todo test with vhfishing and olicon
  const isZasilkovna = false
  const isUlozenka = pack.carrier && pack.carrier.id && pack.carrier.id.includes('ulozenka')
  const isPpl = pack.carrier && pack.carrier.id && pack.carrier.id.includes('ppl')
  const isFedexMultipage = pack.carrier && pack.carrier.id && pack.carrier.id.includes('shippypro_fedex')
  const isPersonally = pack.carrier && pack.carrier.id && pack.carrier.id.includes('personally')
  return printDocument(
    pack.ticketUrl,
    !!isZasilkovna || !!isUlozenka || !!isPpl || !!isFedexMultipage || !!isPersonally,
    true,
    core,
  )
}

export const printDocument = async (
  url: string,
  isLandscape: boolean = false,
  isLabelPrinter: boolean = true,
  core?: DepotoCore,
): Promise<
  | {
      stdout: string
      stderr: string
      error: string
      filePath: string
      fileName: string
      cmd: string
    }
  | any
> => {
  const hasError = await checkTicketError(url, core)
  if (hasError) {
    alert('Chyba při stahování tiskových dat štítku. Ověřte správnost nastavení API dopravce')
    return
  }
  return new Promise(resolve => {
    if (!url || url?.length === 0) {
      return resolve(null)
    }
    if (!isLabelPrinter && !!window.localStorage.getItem('printer2byBrowser')) {
      return core?.services.download.downloadDocumentAndShowInNewWindow(url, true)
    }
    if (window._depoto) {
      const printer = isLabelPrinter ? window.localStorage.getItem('printer') : window.localStorage.getItem('printer2')
      const printerWithParams = `${printer}${isLandscape ? ' -o landscape -o fit-to-page ' : ' -o fit-to-page '}`
      const authorizationHeaderValue = getAuthorizationHeaderValue(core)

      const handleResult = ({ stdout, stderr, error, filePath, fileName, cmd }: any) => {
        resolve({
          stdout,
          stderr,
          error,
          filePath,
          fileName,
          cmd,
        })
      }

      if (authorizationHeaderValue && window._depoto.node.printLPWithAuth) {
        window._depoto.node.printLPWithAuth(url, authorizationHeaderValue, printerWithParams, handleResult)
      } else {
        window._depoto.node.printLP(url, printerWithParams, handleResult)
      }
    } else {
      openFileInNewTabWithAuth(url, core)
        .then(w => {
          resolve({
            stdout: 'window.print()',
            stderr: '',
            error: '',
            filePath: url,
            fileName: '',
            cmd: '',
            window: w || undefined,
          })
        })
        .catch(error => {
          console.error('Failed to open PDF in new tab for printing', error)
          resolve({
            stdout: '',
            stderr: '',
            error: error?.message || 'Failed to open PDF',
            filePath: url,
            fileName: '',
            cmd: '',
            window: null,
          })
        })
      // setTimeout(() => {
      //   if (w && typeof w.print === 'function') {
      //     w.print() // same origin policy. todo workaround by postMessage()
      //   }
      // }, 500)
    }
  })
}

export const checkTicketError = async (url: string, core?: DepotoCore) => {
  if (!url) {
    return true
  }

  const headers = getAuthorizationHeaders(core)

  const attemptRequest = async (method: 'HEAD' | 'GET') => {
    const response = await fetch(url, {
      method,
      headers,
    })
    return response
  }

  try {
    const headResponse = await attemptRequest('HEAD')
    if (headResponse.ok) {
      return false
    }
    // Some endpoints may not allow HEAD, fall back to GET
    const getResponse = await attemptRequest('GET')
    return !getResponse.ok
  } catch (error) {
    try {
      const getResponse = await attemptRequest('GET')
      return !getResponse.ok
    } catch (fallbackError) {
      console.error('checkTicketError failed', fallbackError || error)
      return true
    }
  }
}

export class NumberHelper {
  static fromString(str: string): number {
    if (!str) {
      str = ''
    }
    return Number(str.replace(',', '.').replaceAll(' ', ''))
  }

  static toString(num: number | string, decimals: number = 2): string {
    return `${Number(num).toFixed(decimals)}`
  }
}

// eh, react and the component unloading.... let's do this in router
export const useUnload = (fn: Function) => {
  const cb = useRef(fn)

  useEffect(() => {
    cb.current = fn
  }, [fn])

  useEffect(() => {
    const onUnload = (...args: any) => cb.current?.(...args)
    window.addEventListener('beforeunload', onUnload)
    return () => window.removeEventListener('beforeunload', onUnload)
  }, [])
}

// Barcode scanning functionality has been moved to:
// - src/services/BarcodeScanner.ts
// - src/contexts/BarcodeContext.tsx
// - src/hooks/useBarcode.ts

export const updateOrder = async (
  orderUpdates: Partial<Order>,
  setOrderUpdates: Fn,
  currentOrder: Partial<Order>,
  core: DepotoCore,
  onSuccess: Fn,
) => {
  const updates: any = { ...orderUpdates }
  if (updates?.shippingAddress && currentOrder) {
    let addressId = updates.shippingAddress.id
    if (addressId > 0) {
      await core?.services.address.update(updates.shippingAddress)
    } else {
      const res = await core?.services.address.create(updates.shippingAddress)
      addressId = (res && res.id) || 0
      if (addressId) {
        await core?.services.order.updateOrderPart({
          id: currentOrder.id,
          shippingAddress: new AddressType({ id: addressId }),
        })
      }
    }
  }
  delete updates.shippingAddress
  await core?.services.order.updateOrderPart(updates)
  await wait(100)
  setOrderUpdates({})
  await wait(300)
  setOrderUpdates({})
  onSuccess()
}

export const updatePackages = async (
  packagesUpdates: PackageUpdate,
  setPackageUpdates: Fn,
  core: DepotoCore,
  onSuccess: Fn,
) => {
  const updates = { ...packagesUpdates }
  for (const id in updates) {
    await core?.services.pack.updatePart({ id: Number(id), ...updates[id] })
  }
  await wait(100)
  setPackageUpdates({})
  await wait(300)
  onSuccess()
}

export type GetHeaderTitleFn = (params: { splitLocation: string[] | undefined }) => string

export const getHeaderTitle: GetHeaderTitleFn = ({ splitLocation }) => {
  if (!splitLocation || splitLocation.length <= 1) return ''
  if (splitLocation[1] === 'dashboard') return splitLocation[2]
  if (splitLocation[1] === 'groups' || splitLocation[1] === 'dispatch') return splitLocation[1]
  return ''
}

export const formatDate = (utcString: string, hoursAndMins = false): string => {
  const d = new Date(utcString)
  const dateStr = ('0' + d.getDate()).slice(-2) + '.' + ('0' + (d.getMonth() + 1)).slice(-2) + '.' + d.getFullYear()

  if (hoursAndMins) {
    const timeStr =
      ('0' + d.getHours()).slice(-2) + ':' + ('0' + d.getMinutes()).slice(-2) + ':' + ('0' + d.getSeconds()).slice(-2)
    return dateStr + ' ' + timeStr
  }

  return dateStr
}

// pull OrderItems from ProductBundle
export const getOrderItemsForReturns = (
  order: Order,
  oldReturnedPacks: ProductMovePack[] = [],
): OrderItemForReturn[] => {
  const orderItems: OrderItemForReturn[] = order.items?.filter(oi => oi.type === 'product') || []
  let i = 0
  for (const oi of orderItems) {
    if (oi?.product?.bundleChildren?.length! > 0) {
      delete orderItems[i]
      const children =
        oi.product?.bundleChildren.map(
          (bc: ProductBundle) =>
            new OrderItem({
              id: oi.id,
              name: bc.child?.name,
              product: bc.child,
              code: bc.child?.code,
              ean: bc.child?.ean,
              quantity: bc.quantity * oi.quantity,
              quantityReturned: getQuantityReturnedForOrderItem(oi.id, bc.child?.id, oldReturnedPacks),
            }),
        ) || []
      orderItems.push(...children)
    } else {
      orderItems[i].quantityReturned = getQuantityReturnedForOrderItem(oi.id, oi.product?.id, oldReturnedPacks)
    }
    i++
  }
  return orderItems.filter(oi => oi.quantity > (oi.quantityReturned || 0))
}

const getQuantityReturnedForOrderItem = (
  orderItemId: number,
  productId?: number,
  movePacks: ProductMovePack[] = [],
) => {
  if (movePacks.length === 0) {
    return 0
  }
  const returnsForOrderItem = []
  for (const pmp of movePacks) {
    returnsForOrderItem.push(
      ...pmp.moves.filter((pm: ProductMove) => pm.orderItem?.id === orderItemId && pm.product?.id === productId),
    )
  }
  return returnsForOrderItem.map(pm => pm.amount).reduce((a, b) => a + b, 0)
}

export function mergeById<T extends CommonItem>(newItems: T[], currentItems: T[]) {
  const merged = (currentItems || []).concat(newItems)
  return merged.filter((item, index, self) => index === self.findIndex(found => found.id === item.id))
}

export const isNonEmptyValue = (value: any): boolean => {
  return (
    value !== null &&
    value !== undefined &&
    value !== '' &&
    value !== 0 &&
    (typeof value === 'number' ? !isNaN(value) : true) &&
    (Array.isArray(value) ? value.filter(isNonEmptyValue).length > 0 : true) &&
    (typeof value === 'object' ? Object.values(value).filter(isNonEmptyValue).length > 0 : true)
  )
}

export const removeEmptyProperties = <T>(
  object: T,
  additionalFilter: (val: unknown) => boolean = () => true,
): Partial<NonNullable<T>> =>
  Object.fromEntries(
    Object.entries(object).filter(([_, value]) => isNonEmptyValue(value) && additionalFilter(value)),
  ) as Partial<NonNullable<T>>

export const chooseEnabledFilter = (options: (string | undefined | null | number)[]) =>
  options.reduce((opt, acc) => (isNonEmptyValue(opt) && opt !== 'all' ? opt : acc), null)

export const trimText = (text: string, maxLength: number = 100) => {
  if (text.length <= maxLength) return text
  return text.substring(0, maxLength) + '...'
}

// Compare two objects by their primitives, and return object with changed primitives
export function compareObjectPrimitives(newObject: any, originalObject: any, keys?: string[]) {
  return Object.keys(newObject).reduce((acc, key) => {
    if (keys && !keys.includes(key)) return acc
    if (newObject[key] !== originalObject[key] && typeof newObject[key] !== 'object') {
      acc[key] = newObject[key]
    }
    return acc
  }, {})
}

let isListeningToScale = false
let scaleInitCount = 0
type ScaleCbArgs = { data: string; isFinished: boolean }
const listenForLegacyScaleUpdate = (callback: (args: ScaleCbArgs) => void): void => {
  if (!('_depoto' in window)) {
    callback ? callback({ data: 'not in desktop app', isFinished: true }) : null
    return
  }
  console.log('scale: listening (legacy) ..')
  const spawnedChild = window._depoto.node.spawn('/opt/dymo/dymo-scale')
  spawnedChild.on('exit', (exitCode: string) => {
    callback ? callback({ data: exitCode, isFinished: true }) : null
  })
  spawnedChild.stdout.on('data', (data: any) => {
    if (typeof data !== 'string') {
      data = String(data)
    }
    let weightKg = 0
    if (data.includes('grams')) {
      weightKg = data.split(' grams')[0] / 1000 / 100
    } else if (data.includes('ounces')) {
      weightKg = (data.split(' ounces')[0] * 4.334) / 1000 / 100 // todo: 1oz === 28.3495231g ?
    }
    callback ? callback({ data: weightKg.toFixed(3), isFinished: false }) : null
  })
}

const listenForBuildInScaleUpdate = (callback: (args: ScaleCbArgs) => void): void => {
  if (!('_depoto' in window)) {
    callback ? callback({ data: 'not in desktop app', isFinished: true }) : null
    return
  }
  const isDebug = true
  console.log('scale: listening (nw) ..')
  isListeningToScale = true
  window._depoto.readScale(
    ({ data, isFinal }: { data: { value: number; system: 'grams' | 'ounces' } | string; isFinal: boolean }) => {
      // window._depoto.readScale((data: any) => {
      // console.log('scale: onData:', { data, isFinal })
      if (isFinal) {
        isListeningToScale = false
        callback ? callback({ data: String(data), isFinished: isFinal }) : null
      } else {
        scaleInitCount = 0 // reset on successful data event
        let weightKg = 0
        if (typeof data === 'object' && data.system === 'grams') {
          weightKg = data.value / 1000
        } else if (typeof data === 'object' && data.system === 'ounces') {
          weightKg = (data.value * 4.334) / 1000 // todo: 1oz === 28.3495231g ?
        }
        callback ? callback({ data: weightKg.toFixed(3), isFinished: false }) : null
      }
    },
    isDebug,
  )
}

export const handleScale = () => {
  if (!window.weightHistory) {
    window.weightHistory = []
  }
  setTimeout(() => {
    // wait for nwjs init
    const scaleListenerFn =
      typeof window._depoto?.readScale === 'function' ? listenForBuildInScaleUpdate : listenForLegacyScaleUpdate
    scaleListenerFn(({ data, isFinished }) => {
      // console.log('handleScale: ', { data, isFinished })
      if (!isFinished && typeof data === 'string' && !isNaN(Number(data))) {
        window.weightHistory.push(data)
      } else if (isFinished && data !== 'undefined') {
        scaleInitCount++
        console.warn(`scale error: count: ${scaleInitCount}: isListening: ${isListeningToScale}: msg: ${data}`)
        if (!data.includes('not in desktop app') && scaleInitCount <= 20 && !isListeningToScale) {
          handleScale()
        }
      }
    })
  }, 5000)
}

const APP_STORAGE_PREFIX = 'depoto-expeditions-v2'
const AUTH_DATA_STORAGE_KEY = `${APP_STORAGE_PREFIX}auth_data`
const CLIENT_TYPE_STORAGE_KEY = `${APP_STORAGE_PREFIX}auth_client_type`

const getStoredAuthToken = () => {
  if (typeof window === 'undefined') {
    return undefined
  }
  const serializedToken = window.localStorage.getItem(AUTH_DATA_STORAGE_KEY)
  if (!serializedToken) {
    return undefined
  }
  try {
    const parsed = JSON.parse(serializedToken)
    return parsed?.accessToken || parsed?.access_token
  } catch (error) {
    console.warn('Unable to parse auth token from storage', error)
    return undefined
  }
}

const getStoredClientType = () => {
  if (typeof window === 'undefined') {
    return undefined
  }
  return window.localStorage.getItem(CLIENT_TYPE_STORAGE_KEY) || undefined
}

const getAuthorizationHeaderValue = (core?: DepotoCore) => {
  const tokenFromCore = core?.services?.oauth?.session?.token?.accessToken || core?.services?.oauth?.getAccessToken?.()
  const token = tokenFromCore || getStoredAuthToken()
  if (!token) {
    return undefined
  }
  const clientType = core?.services?.oauth?.session?.clientType || getStoredClientType()
  if (clientType === 'stage') {
    return `Basic ${btoa('stage:stage')}, Bearer ${token}`
  }
  return `Bearer ${token}`
}

const getAuthorizationHeaders = (core?: DepotoCore): HeadersInit | undefined => {
  if (core?.services?.http?.getAuthorizationHeaders) {
    return core.services.http.getAuthorizationHeaders()
  }
  const authorizationValue = getAuthorizationHeaderValue(core)
  if (!authorizationValue) {
    return undefined
  }
  return {
    Authorization: authorizationValue,
    Accept: 'application/json',
    'Content-type': 'application/json',
  }
}

const downloadBlobWithAuth = async (url: string, core?: DepotoCore) => {
  if (!url) {
    throw new Error('File URL is required')
  }
  if (core?.services?.http?.downloadBlob) {
    return core.services.http.downloadBlob(url)
  }

  const headers = getAuthorizationHeaders(core)
  const response = await fetch(url, {
    method: 'POST',
    headers,
  })
  if (!response.ok) {
    throw new Error(`Failed to download file (${response.status})`)
  }
  return response.blob()
}

export const openFileInNewTabWithAuth = async (url: string, core?: DepotoCore) => {
  if (!url) {
    return null
  }
  try {
    const blob = await downloadBlobWithAuth(url, core)
    const objectUrl = window.URL.createObjectURL(blob)
    const openedWindow = window.open(objectUrl, '_blank', 'noopener')
    window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 60_000)
    return openedWindow
  } catch (error) {
    console.error('Failed to open file with auth, falling back to direct URL', error)
    return window.open(url, '_blank', 'noopener')
  }
}

export const downloadFileWithAuth = async (url: string, fileName: string, core?: DepotoCore) => {
  const blob = await downloadBlobWithAuth(url, core)
  const objectUrl = window.URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  document.body.appendChild(anchor)
  anchor.href = objectUrl
  anchor.download = fileName
  anchor.click()
  window.URL.revokeObjectURL(objectUrl)
}

export const matchCountWithWord = (variants: [string, string, string], count: number): string => {
  if (count === 1) {
    return variants[0]
  } else if (count >= 2 && count <= 4) {
    return variants[1]
  } else {
    return variants[2]
  }
}

export const allProcessStatuses: ProcessStates[] = [
  'recieved',
  'picking',
  'packing',
  'packed',
  'dispatched',
  'delivered',
  'returned',
]
export const getNextProcessStatus = (currentStatus?: ProcessStates): ProcessStates => {
  const currentIndex = currentStatus ? allProcessStatuses.indexOf(currentStatus) : 0
  if (currentIndex === allProcessStatuses.length - 1) {
    return allProcessStatuses[allProcessStatuses.length - 1]
  }
  return allProcessStatuses[currentIndex + 1]
}

export const PROCESS_STATUS_NAME = {
  recieved: 'Přijato',
  picking: 'Vyskladnění',
  packing: 'Balení',
  packed: 'Zabaleno',
  dispatched: 'Odesláno',
  delivered: 'Dodáno',
  returned: 'Vráceno',
}

export const areAllOrdersHasSameStatus = (orders: Order[] | undefined): boolean => {
  const statuses = orders?.map(order => order.processStatus?.id)
  return statuses?.every(status => status === statuses[0])
}

export const filterObject = (object: any) => {
  return Object.fromEntries(Object.entries(object).filter(([_, value]) => value !== undefined && value !== null))
}
